import { AsyncStorage } from './AsyncStorage';

export const getAccessToken = async () => AsyncStorage.getItem('accessToken');

export const storeAccessToken = async (accessToken) => {
  Promise.resolve(await AsyncStorage.setItem('accessToken', accessToken));
};

export const deleteAccessToken = () => {
  AsyncStorage.removeItem('accessToken');
};

export const getRefreshToken = async () => AsyncStorage.getItem('refreshToken');

export const storeRefreshToken = async (refreshToken) => {
  Promise.resolve(await AsyncStorage.setItem('refreshToken', refreshToken));
};

export const deleteRefreshToken = async () => {
  await AsyncStorage.removeItem('refreshToken');
};

/**
 * Which provider issued the stored session. A session started on the legacy
 * `/auth` endpoints has no marker (`null`); a session started on the `/oidc`
 * provider is marked `'oidc'`. Refresh, revocation and the apiFetch refresh
 * hooks branch on this marker — never on the remote `useOidc` flag — so a
 * session keeps working across a flag flip in either direction, and
 * `/auth/token` is never called for an `/oidc` session (or vice versa).
 *
 * The `/oidc` session shares the device store with the legacy one (on web:
 * localStorage, so it survives reloads and is shared between tabs). How long
 * a refresh token can renew it is bounded server-side by the provider's
 * refresh-token lifetimes; the apps end it locally and revoke it whenever a
 * re-authentication is required (see revokeOidcSession in ./auth/session.js).
 */
export const TOKEN_PROVIDERS = {
  LEGACY: 'auth',
  OIDC: 'oidc',
};

const TOKEN_PROVIDER_KEY = 'tokenProvider';

export const getTokenProvider = async () => {
  const provider = await AsyncStorage.getItem(TOKEN_PROVIDER_KEY);
  return provider || TOKEN_PROVIDERS.LEGACY;
};

export const storeTokenProvider = async (provider) => {
  await AsyncStorage.setItem(TOKEN_PROVIDER_KEY, provider);
};

export const deleteTokenProvider = async () => {
  await AsyncStorage.removeItem(TOKEN_PROVIDER_KEY);
};

export const isOidcSession = async () =>
  (await getTokenProvider()) === TOKEN_PROVIDERS.OIDC;

/**
 * Persist a session issued by the `/oidc` provider. The refresh token is
 * written first: it rotates on every refresh and the old one is dead the
 * moment the response arrives, so it must be on disk before anything can use
 * the new access token.
 */
export const storeOidcSession = async ({ accessToken, refreshToken }) => {
  if (refreshToken) {
    await AsyncStorage.setItem('refreshToken', refreshToken);
  }
  await AsyncStorage.setItem('accessToken', accessToken);
  await AsyncStorage.setItem(TOKEN_PROVIDER_KEY, TOKEN_PROVIDERS.OIDC);
};

/**
 * Remove every trace of the stored session (access token, refresh token and
 * the provider marker). Used when an `/oidc` session ends — logout, a failed
 * refresh, or the app discarding the access token — so a following legacy
 * login starts from a clean slate.
 */
export const clearStoredSession = async () => {
  await AsyncStorage.removeItem('accessToken');
  await AsyncStorage.removeItem('refreshToken');
  await AsyncStorage.removeItem(TOKEN_PROVIDER_KEY);
};
